import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/Button";
import { FormCard } from "@/components/FormCard";
import { FormState, useAuthStore } from "@/stores/authStore";
import FederatedAuth from "./FederatedAuth";
import { signInSchema } from "@/model/authSchema";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import NewPasswordForm from "./NewPasswordForm";
import { ArrowLeft } from "lucide-react";
import BackButton from "@/components/ui/BackButton";

type TSignInSchema = z.infer<typeof signInSchema>;

type SignInFormProps = {
  handleVerified: () => void;
  handleBack?: () => void;
};

export default function SignInForm({ handleVerified, handleBack }: SignInFormProps) {
  const { setFormState, data } = useAuthStore();
  const { pathname } = useLocation();
  const { handleSignIn } = useAuth();

  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const navigate = useNavigate();

  const form = useForm<TSignInSchema>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: data.email || "",
      password: data.password || "",
    },
  });

  const onSubmit = async (data: TSignInSchema) => {
    const { email, password } = data;
    const nextStep = await handleSignIn(email, password, form, handleVerified);
    if (nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setIsNewPasswordRequired(true);
    } else if (nextStep === "RESET_PASSWORD") {
      navigate(
        `/reset-password?email=${encodeURIComponent(
          email
        )}&redirect=${pathname}`
      );
    }
  };

  return (
    <>
      {!isNewPasswordRequired && (
        <>
          <FormCard
            form={form}
            onSubmit={onSubmit}
            title="Sign in"
            description="Enter your email below to create your account"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="you@example.com"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center justify-between">
                      <span>Password</span>
                      <Link
                        to={`/reset-password${
                          form.watch("email")
                            ? `?email=${encodeURIComponent(
                                form.watch("email")
                              )}`
                            : ""
                        }`}
                        className="hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </FormLabel>
                    <FormControl>
                      <PasswordInput placeholder="********" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {handleBack ? (
                <div className="flex gap-3 justify-start">
                  <BackButton handleBack={handleBack} className="flex-1" buttonClassName="w-full justify-start" />
                  <Button disabled={form.formState.isSubmitting} type="submit" className="flex-1">
                    Sign In
                  </Button>
                </div>
              ) : (
                <Button disabled={form.formState.isSubmitting} type="submit">
                  Sign In
                </Button>
              )}
            </>
            <FederatedAuth prefix="Sign in" />
            {pathname !== "/login" && (
              <div className="mb-10 text-center text-lg">
                <Button
                  onClick={() => setFormState(FormState.SIGNUP)}
                    type="button"
                    className="mt-3 w-full text-black"
                >
                  New Customer? <strong>Sign Up Here</strong>
                  </Button>
              </div>
            )}
          </FormCard>
          
        </>
      )}
      {isNewPasswordRequired &&
        (data.email ? (
          <NewPasswordForm
            defaultEmail={data.email!}
            handleVerified={handleVerified}
            handleCancel={() => setIsNewPasswordRequired(false)}
          />
        ) : (
          <div className="flex justify-center flex-col gap-4 bg-brand-neutral-800 p-6 rounded-md">
            <p className="text-center">
              Email missing or invalid. <br /> Please sign in again.
            </p>
            <Button
              variant="link"
              onClick={() => setIsNewPasswordRequired(false)}
            >
              <ArrowLeft /> Back to Sign In
            </Button>
          </div>
        ))}
    </>
  );
}
